import { useState, useEffect } from 'react';
import { useBroker } from '../contexts/BrokerContext';
import { useLocale } from '../contexts/LocaleContext';
import { Search, UserCheck, UserX, Edit, X, Check, AlertCircle, Settings, ChevronDown, SlidersHorizontal } from 'lucide-react';
import toast from 'react-hot-toast';
import UserOverrideModal from '../components/forms/UserAccountOverrides.jsx';

// ─── Mobile detection ─────────────────────────────────────────────────────────
function useIsMobile(bp = 768) {
  const [is, setIs] = useState(window.innerWidth < bp);
  useEffect(() => {
    const h = () => setIs(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return is;
}

// ─── KYC badge style helper ───────────────────────────────────────────────────
function kycStyle(status) {
  switch (status) {
    case 'verified':      return { bg: 'var(--accent-success)',  alpha: '20', color: 'var(--accent-success)'  };
    case 'pending':       return { bg: 'var(--accent-warning)',  alpha: '20', color: 'var(--accent-warning)'  };
    case 'rejected':      return { bg: 'var(--accent-danger)',   alpha: '20', color: 'var(--accent-danger)'   };
    default:              return { bg: 'var(--text-tertiary)',   alpha: '20', color: 'var(--text-tertiary)'   };
  }
}

// ─── Mobile client card ───────────────────────────────────────────────────────
function ClientCard({ client, onEdit, onOverride, onToggleActive, onUpdateKYC, t }) {
  const [expanded, setExpanded] = useState(false);
  const kyc = kycStyle(client.kyc_status);

  return (
    <div
      className="rounded-2xl border overflow-hidden"
      style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}
    >
      {/* Main row — always visible */}
      <button className="w-full flex items-center gap-3 px-4 py-3.5 text-left" onClick={() => setExpanded(e => !e)}>
        {/* Avatar */}
        <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-black flex-shrink-0"
          style={{ backgroundColor: 'var(--accent-primary)20', color: 'var(--accent-primary)' }}>
          {client.first_name?.[0]}{client.last_name?.[0]}
        </div>

        {/* Name + email */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold truncate" style={{ color: 'var(--text-primary)' }}>
            {client.first_name} {client.last_name}
          </p>
          <p className="text-[11px] truncate" style={{ color: 'var(--text-tertiary)' }}>{client.email}</p>
        </div>

        {/* Balance + chevron */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <p className="text-sm font-black font-mono" style={{ color: 'var(--text-primary)' }}>
            ${parseFloat(client.total_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </p>
          <ChevronDown className="w-4 h-4 transition-transform duration-200"
            style={{ color: 'var(--text-tertiary)', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }} />
        </div>
      </button>

      {/* Status badges row */}
      <div className="flex items-center gap-2 px-4 pb-3">
        {/* KYC */}
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full capitalize"
          style={{ backgroundColor: `color-mix(in srgb, ${kyc.bg} 15%, transparent)`, color: kyc.color, border: `1px solid color-mix(in srgb, ${kyc.bg} 30%, transparent)` }}>
          {client.kyc_status?.replace('_', ' ') || t('kyc.pending').toLowerCase()}
        </span>
        {/* Active */}
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1"
          style={{
            backgroundColor: client.is_active ? 'color-mix(in srgb, var(--accent-success) 15%, transparent)' : 'color-mix(in srgb, var(--text-tertiary) 15%, transparent)',
            color: client.is_active ? 'var(--accent-success)' : 'var(--text-tertiary)',
          }}>
          {client.is_active ? <UserCheck className="w-2.5 h-2.5" /> : <UserX className="w-2.5 h-2.5" />}
          {client.is_active ? t('common.active') : t('common.inactive')}
        </span>
        {/* Accounts */}
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
          style={{ backgroundColor: 'color-mix(in srgb, var(--accent-info) 15%, transparent)', color: 'var(--accent-info)' }}>
          {(client.live_accounts || 0) + (client.demo_accounts || 0)} {t('nav.accounts').toLowerCase()}
        </span>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t px-4 py-3 space-y-3"
          style={{ borderColor: 'var(--border-color)', backgroundColor: 'color-mix(in srgb, var(--bg-tertiary) 40%, transparent)' }}>

          {/* Detail rows */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{t('common.phone')}</p>
              <p className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{client.phone || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>ID</p>
              <p className="text-xs font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>#{client.id}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{t('accounts.live')}</p>
              <p className="text-xs font-semibold" style={{ color: 'var(--accent-success)' }}>{client.live_accounts || 0} {t('nav.accounts').toLowerCase()}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>{t('accounts.demo')}</p>
              <p className="text-xs font-semibold" style={{ color: 'var(--accent-info)' }}>{client.demo_accounts || 0} {t('nav.accounts').toLowerCase()}</p>
            </div>
          </div>

          {/* KYC inline selector */}
          <div>
            <p className="text-[10px] uppercase tracking-wide mb-1" style={{ color: 'var(--text-tertiary)' }}>{t('clients.kycStatus')}</p>
            <select
              value={client.kyc_status || 'not_submitted'}
              onChange={e => onUpdateKYC(client.id, e.target.value)}
              className="w-full px-3 py-2 rounded-xl text-xs font-semibold border focus:outline-none"
              style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            >
              <option value="not_submitted">{t('kyc.pending')}</option>
              <option value="pending">{t('kyc.pending')}</option>
              <option value="verified">{t('kyc.verified')}</option>
              <option value="rejected">{t('kyc.rejected')}</option>
            </select>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 pt-1">
            <button onClick={() => onToggleActive(client)}
              className="flex-1 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all active:scale-95"
              style={{
                backgroundColor: client.is_active ? 'color-mix(in srgb, var(--accent-danger) 12%, transparent)' : 'color-mix(in srgb, var(--accent-success) 12%, transparent)',
                color: client.is_active ? 'var(--accent-danger)' : 'var(--accent-success)',
                border: `1px solid color-mix(in srgb, ${client.is_active ? 'var(--accent-danger)' : 'var(--accent-success)'} 25%, transparent)`,
              }}>
              {client.is_active ? <UserX className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}
              {client.is_active ? t('common.disabled') : t('common.enabled')}
            </button>
            <button onClick={() => onEdit(client)}
              className="px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all active:scale-95"
              style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
              <Edit className="w-3.5 h-3.5" />{t('common.edit')}
            </button>
            <button onClick={() => onOverride(client)}
              className="px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all active:scale-95"
              style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 12%, transparent)', color: 'var(--accent-primary)', border: '1px solid color-mix(in srgb, var(--accent-primary) 25%, transparent)' }}>
              <Settings className="w-3.5 h-3.5" />{t('clients.configureOverride')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Mobile filter drawer ─────────────────────────────────────────────────────
function FilterDrawer({ filters, setFilters, searchTerm, setSearchTerm, onSearch, onClose, onClear, hasActive, t }) {
  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative rounded-t-2xl pt-4 pb-8 z-10 max-h-[80vh] overflow-y-auto"
        style={{ backgroundColor: 'var(--bg-secondary)' }}>
        <div className="w-10 h-1 rounded-full mx-auto mb-4" style={{ backgroundColor: 'var(--border-color)' }} />
        <div className="flex items-center justify-between px-4 mb-5">
          <h3 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>{t('common.filter')}</h3>
          {hasActive && (
            <button onClick={onClear} className="text-xs font-semibold flex items-center gap-1"
              style={{ color: 'var(--accent-primary)' }}>
              <X className="w-3.5 h-3.5" />{t('common.clearAll')}
            </button>
          )}
        </div>
        <div className="px-4 space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-secondary)' }}>{t('common.search')}</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                placeholder={t('clients.nameOrEmail')}
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border focus:outline-none text-sm"
                style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-secondary)' }}>{t('common.status')}</label>
            <select value={filters.is_active === undefined ? '' : String(filters.is_active)}
              onChange={e => setFilters(f => ({ ...f, is_active: e.target.value === '' ? undefined : e.target.value === 'true' }))}
              className="w-full px-3 py-2.5 rounded-xl border focus:outline-none text-sm"
              style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
              <option value="">{t('kyc.allStatus')}</option>
              <option value="true">{t('common.active')} {t('common.only')}</option>
              <option value="false">{t('common.inactive')} {t('common.only')}</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: 'var(--text-secondary)' }}>{t('clients.kycStatus')}</label>
            <select value={filters.kyc_status}
              onChange={e => setFilters(f => ({ ...f, kyc_status: e.target.value }))}
              className="w-full px-3 py-2.5 rounded-xl border focus:outline-none text-sm"
              style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
              <option value="">{t('kyc.allKycStatus')}</option>
              <option value="verified">{t('kyc.verified')}</option>
              <option value="pending">{t('kyc.pending')}</option>
              <option value="rejected">{t('kyc.rejected')}</option>
              <option value="not_submitted">{t('kyc.pending')}</option>
            </select>
          </div>
          <button onClick={() => { onSearch(); onClose(); }}
            className="w-full py-3 rounded-xl text-sm font-bold text-white mt-1"
            style={{ backgroundColor: 'var(--accent-primary)' }}>
            {t('common.apply')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function Clients() {
  const { t } = useLocale();
  const isMobile = useIsMobile();
  const { fetchClients, updateClient, fetchUserOverride, upsertUserOverride, deleteUserOverride } = useBroker();

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ is_active: undefined, kyc_status: '' });
  const [showFilters, setShowFilters] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [selectedUserForOverride, setSelectedUserForOverride] = useState(null);
  const [userOverride, setUserOverride] = useState(null);

  useEffect(() => { loadClients(); }, [filters]);

  const loadClients = async () => {
    setLoading(true);
    try {
      const response = await fetchClients({ search: searchTerm, ...filters });
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Error loading clients:', error);
      toast.error(t('errors.actionFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => { e?.preventDefault(); loadClients(); };

  const handleToggleActive = async (client) => {
    try {
      await updateClient(client.id, { is_active: client.is_active ? 0 : 1 });
      toast.success(t('success.updated'));
      loadClients();
    } catch { 
      toast.error(t('errors.actionFailed')); 
    }
  };

  const handleUpdateKYC = async (clientId, newStatus) => {
    try {
      await updateClient(clientId, { kyc_status: newStatus });
      toast.success(t('success.updated'));
      loadClients();
    } catch { 
      toast.error(t('errors.actionFailed')); 
    }
  };

  const handleOpenEdit = (client) => { setEditingClient(client); setShowEditModal(true); };

  const handleOpenOverrideModal = async (client) => {
    setSelectedUserForOverride(client);
    try {
      const response = await fetchUserOverride(client.id);
      const overrideData = response?.data?.override || response?.data || response?.override || null;
      setUserOverride(overrideData && Object.keys(overrideData).length > 0 ? overrideData : null);
    } catch { setUserOverride(null); }
    setShowOverrideModal(true);
  };

  const handleSaveUserOverride = async (overrideData) => {
    try {
      await upsertUserOverride(selectedUserForOverride.id, overrideData);
      toast.success(t('success.saved'));
      setShowOverrideModal(false);
      setSelectedUserForOverride(null);
      setUserOverride(null);
      loadClients();
    } catch (error) {
      toast.error(error.message || t('errors.actionFailed'));
      throw error;
    }
  };

  const filteredClients = clients.filter(client => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return client.email?.toLowerCase().includes(s) ||
      client.first_name?.toLowerCase().includes(s) ||
      client.last_name?.toLowerCase().includes(s);
  });

  const stats = {
    total:        clients.length,
    active:       clients.filter(c => c.is_active).length,
    verified:     clients.filter(c => c.kyc_status === 'verified').length,
    totalBalance: clients.reduce((sum, c) => sum + parseFloat(c.total_balance || 0), 0),
  };

  const hasActiveFilters = filters.is_active !== undefined || filters.kyc_status || searchTerm;

  const clearFilters = () => { setFilters({ is_active: undefined, kyc_status: '' }); setSearchTerm(''); };

  // ══════════════════════════════════════════════════════════════════════════
  // MOBILE
  // ══════════════════════════════════════════════════════════════════════════
  if (isMobile) {
    return (
      <div className="flex flex-col -mx-4 -my-6 min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>

        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <div className="px-4 pt-5 pb-6" style={{
          background: 'linear-gradient(135deg, var(--accent-primary) 0%, color-mix(in srgb, var(--accent-primary) 60%, var(--accent-info)) 100%)'
        }}>
          <p className="text-[11px] font-bold uppercase tracking-widest mb-1" style={{ color: 'rgba(255,255,255,0.6)' }}>
            {t('clients.title')}
          </p>
          <p className="text-4xl font-black text-white leading-none font-mono">{stats.total}</p>
          <p className="text-xs mt-1.5" style={{ color: 'rgba(255,255,255,0.6)' }}>{t('dashboard.totalClients')}</p>

          {/* 3 stat chips */}
          <div className="grid grid-cols-3 gap-2 mt-4">
            {[
              { label: t('common.active'),   value: stats.active,   color: 'rgba(255,255,255,0.9)' },
              { label: t('kyc.verified'), value: stats.verified, color: 'rgba(255,255,255,0.9)' },
              { label: t('accounts.balance'),  value: `$${(stats.totalBalance / 1000).toFixed(1)}k`, color: 'rgba(255,255,255,0.9)' },
            ].map(({ label, value, color }) => (
              <div key={label} className="rounded-xl px-3 py-2.5 text-center"
                style={{ backgroundColor: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)' }}>
                <p className="text-sm font-black" style={{ color }}>{value}</p>
                <p className="text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.55)' }}>{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Search bar + filter button ────────────────────────────────── */}
        <div className="flex items-center gap-2 px-4 py-3 border-b" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}>
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
            <input type="text" value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && loadClients()}
              placeholder={t('clients.searchPlaceholder')}
              className="w-full pl-9 pr-3 py-2 rounded-xl border text-sm focus:outline-none"
              style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
          </div>
          <button onClick={() => setShowFilters(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-bold relative"
            style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
            <SlidersHorizontal className="w-4 h-4" />
            {t('common.filter')}
            {hasActiveFilters && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[9px] font-black flex items-center justify-center text-white"
                style={{ backgroundColor: 'var(--accent-primary)' }}>
                {[filters.is_active !== undefined, !!filters.kyc_status, !!searchTerm].filter(Boolean).length}
              </span>
            )}
          </button>
        </div>

        {/* ── Active filter chips ───────────────────────────────────────── */}
        {hasActiveFilters && (
          <div className="flex items-center gap-2 px-4 py-2 overflow-x-auto border-b" style={{ borderColor: 'var(--border-color)' }}>
            {searchTerm && (
              <div className="flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full flex-shrink-0"
                style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 15%, transparent)', color: 'var(--accent-primary)' }}>
                "{searchTerm}"
                <button onClick={() => setSearchTerm('')}><X className="w-2.5 h-2.5" /></button>
              </div>
            )}
            {filters.is_active !== undefined && (
              <div className="flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full flex-shrink-0"
                style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 15%, transparent)', color: 'var(--accent-primary)' }}>
                {filters.is_active ? t('common.active') : t('common.inactive')}
                <button onClick={() => setFilters(f => ({ ...f, is_active: undefined }))}><X className="w-2.5 h-2.5" /></button>
              </div>
            )}
            {filters.kyc_status && (
              <div className="flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full flex-shrink-0"
                style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 15%, transparent)', color: 'var(--accent-primary)' }}>
                {t('nav.kyc')}: {filters.kyc_status}
                <button onClick={() => setFilters(f => ({ ...f, kyc_status: '' }))}><X className="w-2.5 h-2.5" /></button>
              </div>
            )}
          </div>
        )}

        {/* ── Result count ─────────────────────────────────────────────── */}
        <div className="px-4 py-2 border-b" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}>
          <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
            {filteredClients.length} {t('clients.title').toLowerCase()}
            {filteredClients.length !== clients.length ? ` ${t('common.of')} ${clients.length}` : ''}
          </p>
        </div>

        {/* ── Client list ───────────────────────────────────────────────── */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: 'var(--accent-primary)', borderTopColor: 'transparent' }} />
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <AlertCircle className="w-10 h-10 mb-3" style={{ color: 'var(--text-tertiary)' }} />
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t('clients.noClientsFound')}</p>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="mt-2 text-xs font-semibold" style={{ color: 'var(--accent-primary)' }}>
                {t('common.clearAll')}
              </button>
            )}
          </div>
        ) : (
          <div className="px-4 pt-3 pb-8 space-y-2">
            {filteredClients.map(client => (
              <ClientCard
                key={client.id}
                client={client}
                onEdit={handleOpenEdit}
                onOverride={handleOpenOverrideModal}
                onToggleActive={handleToggleActive}
                onUpdateKYC={handleUpdateKYC}
                t={t}
              />
            ))}
          </div>
        )}

        {/* Filter drawer */}
        {showFilters && (
          <FilterDrawer
            filters={filters}
            setFilters={setFilters}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onSearch={loadClients}
            onClose={() => setShowFilters(false)}
            onClear={clearFilters}
            hasActive={hasActiveFilters}
            t={t}
          />
        )}

        {/* Modals */}
        {showEditModal && editingClient && (
          <ClientEditModal
            client={editingClient}
            onClose={() => { setShowEditModal(false); setEditingClient(null); }}
            onSave={async (updates) => {
              try {
                await updateClient(editingClient.id, updates);
                toast.success(t('success.updated'));
                setShowEditModal(false);
                loadClients();
              } catch { 
                toast.error(t('errors.actionFailed')); 
              }
            }}
            t={t}
          />
        )}
        {showOverrideModal && selectedUserForOverride && (
          <UserOverrideModal
            user={selectedUserForOverride}
            existingOverride={userOverride}
            onClose={() => { setShowOverrideModal(false); setSelectedUserForOverride(null); setUserOverride(null); }}
            onSave={handleSaveUserOverride}
          />
        )}

        <div className="pb-[env(safe-area-inset-bottom)]" />
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // DESKTOP
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold mb-2">{t('clients.title')}</h1>
        <p style={{ color: 'var(--text-secondary)' }}>{t('clients.clientsList')}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
          <p className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>{t('dashboard.totalClients')}</p>
          <p className="text-2xl font-bold">{stats.total}</p>
        </div>
        <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
          <p className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>{t('dashboard.activeClients')}</p>
          <p className="text-2xl font-bold" style={{ color: 'var(--accent-success)' }}>{stats.active}</p>
        </div>
        <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
          <p className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>{t('kyc.verified')}</p>
          <p className="text-2xl font-bold" style={{ color: 'var(--accent-primary)' }}>{stats.verified}</p>
        </div>
        <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
          <p className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>{t('clients.totalBalance')}</p>
          <p className="text-2xl font-bold">${stats.totalBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: 'var(--text-tertiary)' }} />
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              placeholder={t('clients.searchPlaceholder')}
              className="w-full pl-10 pr-4 py-2 rounded-lg border focus:outline-none"
              style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
          </div>
        </form>
        <select value={filters.is_active === undefined ? '' : filters.is_active}
          onChange={e => setFilters({ ...filters, is_active: e.target.value === '' ? undefined : e.target.value === 'true' })}
          className="px-4 py-2 rounded-lg border focus:outline-none"
          style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
          <option value="">{t('kyc.allStatus')}</option>
          <option value="true">{t('common.active')} {t('common.only')}</option>
          <option value="false">{t('common.inactive')} {t('common.only')}</option>
        </select>
        <select value={filters.kyc_status}
          onChange={e => setFilters({ ...filters, kyc_status: e.target.value })}
          className="px-4 py-2 rounded-lg border focus:outline-none"
          style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
          <option value="">{t('kyc.allKycStatus')}</option>
          <option value="verified">{t('kyc.verified')}</option>
          <option value="pending">{t('kyc.pending')}</option>
          <option value="rejected">{t('kyc.rejected')}</option>
          <option value="not_submitted">{t('kyc.pending')}</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-16 h-16 border-4 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: 'var(--accent-primary)', borderTopColor: 'transparent' }} />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--border-color)' }}>
          <table className="w-full">
            <thead>
              <tr className="border-b" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
                <th className="px-4 py-3 text-left text-sm font-semibold">{t('clients.name')}</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">{t('common.email')}</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">{t('nav.accounts')}</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">{t('accounts.balance')}</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">{t('clients.kycStatus')}</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">{t('common.status')}</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center" style={{ color: 'var(--text-secondary)' }}>{t('clients.noClientsFound')}</td>
                </tr>
              ) : filteredClients.map(client => (
                <tr key={client.id} className="border-b transition-smooth"
                  style={{ borderColor: 'var(--border-color)' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center font-semibold"
                        style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 20%, transparent)', color: 'var(--accent-primary)' }}>
                        {client.first_name?.[0]}{client.last_name?.[0]}
                      </div>
                      <div>
                        <p className="font-medium">{client.first_name} {client.last_name}</p>
                        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>ID: {client.id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm">{client.email}</p>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{client.phone || t('common.notAvailable')}</p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <span className="px-2 py-1 rounded text-xs font-medium"
                        style={{ backgroundColor: 'color-mix(in srgb, var(--accent-success) 20%, transparent)', color: 'var(--accent-success)' }}>
                        {client.live_accounts || 0} {t('accounts.live')}
                      </span>
                      <span className="px-2 py-1 rounded text-xs font-medium"
                        style={{ backgroundColor: 'color-mix(in srgb, var(--accent-info) 20%, transparent)', color: 'var(--accent-info)' }}>
                        {client.demo_accounts || 0} {t('accounts.demo')}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-mono font-semibold">
                      ${parseFloat(client.total_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <select value={client.kyc_status || 'not_submitted'}
                      onChange={e => handleUpdateKYC(client.id, e.target.value)}
                      className="px-2 py-1 rounded text-xs font-medium border-0 cursor-pointer"
                      style={{
                        backgroundColor: client.kyc_status === 'verified'  ? 'color-mix(in srgb, var(--accent-success) 20%, transparent)'
                          : client.kyc_status === 'pending'   ? 'color-mix(in srgb, var(--accent-warning) 20%, transparent)'
                          : client.kyc_status === 'rejected'  ? 'color-mix(in srgb, var(--accent-danger) 20%, transparent)'
                          : 'color-mix(in srgb, var(--text-tertiary) 20%, transparent)',
                        color: client.kyc_status === 'verified'  ? 'var(--accent-success)'
                          : client.kyc_status === 'pending'   ? 'var(--accent-warning)'
                          : client.kyc_status === 'rejected'  ? 'var(--accent-danger)'
                          : 'var(--text-tertiary)',
                      }}>
                      <option value="not_submitted">{t('kyc.pending')}</option>
                      <option value="pending">{t('kyc.pending')}</option>
                      <option value="verified">{t('kyc.verified')}</option>
                      <option value="rejected">{t('kyc.rejected')}</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => handleToggleActive(client)}
                      className="px-3 py-1 rounded text-xs font-medium flex items-center gap-1"
                      style={{
                        backgroundColor: client.is_active ? 'color-mix(in srgb, var(--accent-success) 20%, transparent)' : 'color-mix(in srgb, var(--text-tertiary) 20%, transparent)',
                        color: client.is_active ? 'var(--accent-success)' : 'var(--text-tertiary)',
                      }}>
                      {client.is_active ? <UserCheck className="w-3 h-3" /> : <UserX className="w-3 h-3" />}
                      {client.is_active ? t('common.active') : t('common.inactive')}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleOpenEdit(client)}
                        className="p-2 rounded-lg transition-smooth"
                        style={{ color: 'var(--text-primary)' }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
                        title={t('clients.editClient')}>
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleOpenOverrideModal(client)}
                        className="p-2 rounded-lg transition-smooth"
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
                        title={t('clients.configureOverride')}>
                        <Settings className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showEditModal && editingClient && (
        <ClientEditModal
          client={editingClient}
          onClose={() => { setShowEditModal(false); setEditingClient(null); }}
          onSave={async (updates) => {
            try {
              await updateClient(editingClient.id, updates);
              toast.success(t('success.updated'));
              setShowEditModal(false);
              loadClients();
            } catch { 
              toast.error(t('errors.actionFailed')); 
            }
          }}
          t={t}
        />
      )}
      {showOverrideModal && selectedUserForOverride && (
        <UserOverrideModal
          user={selectedUserForOverride}
          existingOverride={userOverride}
          onClose={() => { setShowOverrideModal(false); setSelectedUserForOverride(null); setUserOverride(null); }}
          onSave={handleSaveUserOverride}
        />
      )}
    </div>
  );
}

// ─── Client Edit Modal ────────────────────────────────────────────────────────
function ClientEditModal({ client, onClose, onSave, t }) {
  const [formData, setFormData] = useState({
    first_name: client.first_name || '',
    last_name:  client.last_name  || '',
    email:      client.email      || '',
    phone:      client.phone      || '',
    country:    client.country    || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(formData); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="rounded-xl max-w-md w-full p-6" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">{t('clients.editClient')}</h3>
          <button onClick={onClose} className="p-2 rounded-lg"
            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {[
            { label: t('common.name'), key: 'first_name', type: 'text',  required: true  },
            { label: t('clients.name'), key: 'last_name',  type: 'text',  required: true  },
            { label: t('common.email'), key: 'email', type: 'email', required: true  },
            { label: t('common.phone'), key: 'phone', type: 'tel',   required: false },
            { label: t('common.country'), key: 'country', type: 'text',  required: false, maxLength: 2, placeholder: 'US' },
          ].map(({ label, key, type, required, maxLength, placeholder }) => (
            <div key={key}>
              <label className="block text-sm font-medium mb-2">{label}</label>
              <input type={type} value={formData[key]} required={required}
                maxLength={maxLength} placeholder={placeholder}
                onChange={e => setFormData({ ...formData, [key]: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border focus:outline-none"
                style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            </div>
          ))}
          <div className="flex justify-end gap-3 mt-6">
            <button type="button" onClick={onClose}
              className="px-6 py-2 rounded-lg border transition-smooth"
              style={{ borderColor: 'var(--border-color)' }}>
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={saving}
              className="px-6 py-2 rounded-lg text-white disabled:opacity-50"
              style={{ backgroundColor: 'var(--accent-primary)' }}>
              {saving ? t('settings.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}